<script setup>
// PublicHome — the signed-out storefront: a shallow hero (headline + search +
// mic test), category chips, and the deck catalogue. This REPLACES the old
// public view of QuizzesDecks (the H1 + tab strip + blue system notice + the
// Domain→Subject→Topic drill-down), which was an application surface pointed at
// a first-time visitor.
//
// The drill-down picker is NOT deleted — signed-in users still get it at
// /topics. This is a flat, browsable catalogue, which is what a visitor wants.
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import DeckCoverCard from "../quizzes/DeckCoverCard.vue";
import { buildChips, applyCatalogFilters, difficultyOptions } from "../../lib/publicCatalog.js";

const router = useRouter();

// The value props. They live HERE, not in PublicLayout, so their position is
// deliberate: PublicHome has two root elements, so a sibling section in the
// layout renders *inside* router-view's fragment — the strip ended up between
// the hero and the catalogue by accident rather than by instruction.
const FEATURES = [
  { icon: "bi-mic", tint: "#2563eb", title: "Speak your answers", body: "Answer out loud using our voice interface." },
  { icon: "bi-lightbulb", tint: "#0d9488", title: "Boost your memory", body: "Reinforce knowledge with smart repetition." },
  { icon: "bi-graph-up-arrow", tint: "#7c3aed", title: "Track your progress", body: "See how you're improving over time." },
  { icon: "bi-person", tint: "#ea580c", title: "Learn anywhere", body: "On any device, at your own pace." }
];

const loading = ref(true);
const error = ref("");
const decks = ref([]);

const query = ref("");
const chip = ref(null);          // subject slug, null = All decks
const difficulty = ref(null);    // band key, null = All
const chipsExpanded = ref(false);

const CHIP_LIMIT = 5; // beyond this they collapse behind "More"

const chips = computed(() => buildChips(decks.value));
const visibleChips = computed(() =>
  chipsExpanded.value ? chips.value : chips.value.slice(0, CHIP_LIMIT + 1)); // +1 = "All decks"
const hiddenCount = computed(() => Math.max(0, chips.value.length - (CHIP_LIMIT + 1)));

const diffOptions = computed(() => difficultyOptions(decks.value));
const results = computed(() =>
  applyCatalogFilters(decks.value, { chip: chip.value, query: query.value, difficulty: difficulty.value }));

// The card's own icon rules, reused so a chip and its cards agree.
const CHIP_ICONS = [
  [/first.?aid|cpr|bleed|burn/, "bi-bandaid"],
  [/cardio|heart|health|medic|anatom/, "bi-heart-pulse"],
  [/driver|driving|road|traffic/, "bi-car-front"],
  [/geo|world|continent|capital/, "bi-globe-americas"],
  [/tech|program|comput|code/, "bi-code-slash"],
  [/agri|nature|animal|farm|sheep/, "bi-tree"]
];
function chipIcon(c) {
  if (!c.slug) return "bi-grid-fill";
  for (const [re, icon] of CHIP_ICONS) if (re.test(c.slug)) return icon;
  return "bi-journal-text";
}

const DOMAIN_COLOR = { healthcare: "#0d9488", "road-safety": "#ea580c" };
const deckColor = (d) => DOMAIN_COLOR[d.category?.[0]] || "#2563eb";
const playHref = (slug) => `/topics/play/${encodeURIComponent(slug)}`;

function clearAll() { query.value = ""; chip.value = null; difficulty.value = null; }

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const res = await fetch("/api/decks", { credentials: "include" });
    if (!res.ok) throw new Error(`/api/decks responded ${res.status}`);
    decks.value = (await res.json()).decks || [];
  } catch (e) {
    console.error("[public] deck load failed:", e);
    error.value = "We couldn't load the topics just now. Please refresh.";
  } finally {
    loading.value = false;
  }
}
onMounted(load);
</script>

<template>
  <!-- Hero ---------------------------------------------------------------- -->
  <section class="hero">
    <div class="hero-wrap">
      <div class="hero-copy">
        <h1>What would you like<br />to <em>remember?</em></h1>
        <p class="hero-sub">Choose a topic, answer out loud and strengthen your recall.</p>

        <div class="hero-tools">
          <div class="hero-search">
            <i class="bi bi-search"></i>
            <input
              v-model="query" type="search" autocomplete="off"
              placeholder="Search decks and topics..." aria-label="Search decks and topics" />
          </div>
          <router-link to="/device-check" class="hero-mic" title="Check your microphone">
            <i class="bi bi-mic-fill"></i><span>Test microphone</span>
          </router-link>
        </div>

        <p class="hero-note">
          <i class="bi bi-stars"></i>
          <span>No account needed to begin. Two attempts per question,<br class="d-none d-md-inline" />
            followed by the answer and explanation.</span>
        </p>
      </div>

    </div>
  </section>

  <!-- Value props — a card riding the hero's lower edge, so the promise sits
       with the pitch rather than being discovered at the bottom of the page. -->
  <section id="how-it-works" class="feats-wrap">
    <div class="feats">
      <div v-for="f in FEATURES" :key="f.title" class="feat">
        <span class="feat-ic" :style="{ color: f.tint, background: f.tint + '14' }">
          <i class="bi" :class="f.icon"></i>
        </span>
        <div>
          <h3>{{ f.title }}</h3>
          <p>{{ f.body }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Catalogue ------------------------------------------------------------ -->
  <section class="cat">
    <div class="cat-bar">
      <div class="chips">
        <button
          v-for="c in visibleChips" :key="c.slug || 'all'"
          type="button" class="chip" :class="{ on: chip === c.slug }"
          @click="chip = c.slug">
          <i class="bi" :class="chipIcon(c)"></i>
          <span>{{ c.label }}</span>
        </button>
        <button
          v-if="hiddenCount && !chipsExpanded" type="button" class="chip chip-more"
          @click="chipsExpanded = true">
          <span>More</span><i class="bi bi-chevron-down"></i>
        </button>
      </div>

      <label v-if="diffOptions.length" class="diff">
        <span>Difficulty:</span>
        <select v-model="difficulty">
          <option :value="null">All</option>
          <option v-for="o in diffOptions" :key="o.key" :value="o.key">{{ o.label }}</option>
        </select>
      </label>
    </div>

    <p v-if="loading" class="cat-msg">Loading topics…</p>
    <p v-else-if="error" class="cat-msg cat-err">{{ error }}</p>

    <div v-else-if="results.length" class="deck-grid">
      <DeckCoverCard
        v-for="d in results" :key="d.slug"
        :deck="d" :color="deckColor(d)" @play="router.push(playHref(d.slug))" />
    </div>

    <p v-else class="cat-msg">
      No topics match that. <button type="button" class="linkish" @click="clearAll">Clear filters</button>.
    </p>
  </section>
</template>

<style scoped>
/* --- hero: pale, quiet. The CARDS are the saturated objects on this page; a
   dark hero would fight them and make the whole thing feel heavy.
   The designer's artwork IS the band's background — it's a full-bleed 16:9 field
   whose left two-thirds are deliberately empty for exactly this copy, so it's a
   background-image, not an <img> in a column. Sized to cover and pinned RIGHT so
   the headphones hold their place while the clear area stretches under the text.
   (Served as WebP: the source PNG was 1.2 MB, which is a real cost on the one
   page where first impressions are formed. Same pixels, 20 KB.) --- */
.hero {
  background-color: #dfeafb; /* matches the artwork's mid-tone; fills any gap */
  background-image: url("/images/hero-headset.webp");
  background-size: cover;
  background-position: right center;
  background-repeat: no-repeat;
  border-bottom: 1px solid #e8edf5;
}
/* Top padding clears the OVERLAID header (the logo + menu now sit on this
   artwork rather than in a bar above it), so the headline starts just under the
   logo. Bottom padding = room for the value-prop card that rides up over this
   edge (see .feats); without it the card would sit on the hero's own copy. */
.hero-wrap { max-width: 1180px; margin: 0 auto; padding: 9.5rem 1.25rem 5.5rem;
  display: grid; grid-template-columns: minmax(0, 1fr); }
/* The copy occupies the artwork's clear zone. Capping it keeps the headline off
   the headphones at every width. */
.hero-copy { min-width: 0; max-width: 34rem; }
.hero h1 { margin: 0; font-size: clamp(2.1rem, 4.4vw, 3.4rem); line-height: 1.08; font-weight: 800; letter-spacing: -.02em; color: #0f172a; }
.hero h1 em { font-style: italic; font-weight: 700; color: #2563eb; }
.hero-sub { margin: 1rem 0 0; font-size: 1.06rem; color: #475569; }

.hero-tools { display: flex; gap: .75rem; margin-top: 1.6rem; flex-wrap: wrap; }
.hero-search { position: relative; flex: 1 1 320px; max-width: 380px; }
.hero-search .bi { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.hero-search input {
  width: 100%; padding: .95rem 1rem .95rem 2.75rem; font-size: 1rem;
  border: 1px solid #dbe3ee; border-radius: 12px; background: #fff; color: #0f172a;
  box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.hero-search input::placeholder { color: #94a3b8; }
.hero-search input:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.14); }
.hero-mic {
  display: inline-flex; align-items: center; gap: .55rem; white-space: nowrap;
  padding: .95rem 1.25rem; border: 1px solid #dbe3ee; border-radius: 12px;
  background: #fff; color: #2563eb; font-weight: 600; text-decoration: none;
  box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.hero-mic:hover { border-color: #2563eb; }

.hero-note { display: flex; gap: .6rem; margin: 1.5rem 0 0; color: #64748b; font-size: .95rem; line-height: 1.45; }
.hero-note .bi { color: #2563eb; }

/* --- value props: a card riding the hero's lower edge ---
   The negative top margin is what pulls it up onto the banner. The hero carries
   extra bottom padding to make room, so the overlap never eats the copy. --- */
.feats-wrap { max-width: 1180px; margin: 0 auto; padding: 0 1.25rem; position: relative; z-index: 2; }
.feats {
  display: grid; grid-template-columns: repeat(4, 1fr);
  background: #fff; border: 1px solid #e8edf5; border-radius: 16px;
  padding: 1.6rem 0; margin-top: -2.75rem;
  box-shadow: 0 10px 30px rgba(15,23,42,.09), 0 2px 6px rgba(15,23,42,.05);
}
.feat { display: flex; gap: .9rem; align-items: flex-start; padding: 0 1.6rem; }
.feat + .feat { border-left: 1px solid #eef2f7; }
.feat-ic { flex: none; width: 42px; height: 42px; border-radius: 12px; display: grid; place-items: center; font-size: 1.3rem; }
.feat h3 { margin: 0 0 .2rem; font-size: 1rem; font-weight: 700; color: #0f172a; }
.feat p { margin: 0; font-size: .9rem; line-height: 1.4; color: #64748b; }

/* --- catalogue --- */
.cat { max-width: 1180px; margin: 0 auto; padding: 2.5rem 1.25rem 0; }
.cat-bar { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.6rem; }
.chips { display: flex; gap: .6rem; flex-wrap: wrap; flex: 1; }
.chip {
  display: inline-flex; align-items: center; gap: .5rem;
  padding: .62rem 1rem; border-radius: 999px; font-size: .95rem; font-weight: 600;
  background: #fff; color: #334155; border: 1px solid #e2e8f0; cursor: pointer;
  transition: border-color .12s ease, box-shadow .12s ease;
}
.chip:hover { border-color: #c7d5ea; box-shadow: 0 2px 8px rgba(15,23,42,.06); }
.chip .bi { color: #94a3b8; }
.chip.on { background: #2563eb; border-color: #2563eb; color: #fff; box-shadow: 0 3px 12px rgba(37,99,235,.28); }
.chip.on .bi { color: #fff; }
.chip-more { color: #64748b; }

.diff { display: inline-flex; align-items: center; gap: .55rem; font-size: .95rem; color: #64748b; }
.diff select {
  padding: .55rem 2rem .55rem .8rem; border: 1px solid #e2e8f0; border-radius: 10px;
  background: #fff; color: #0f172a; font-size: .95rem; font-weight: 600;
}
.diff select:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.14); }

/* Same caps as the signed-in picker: 300px cards, 170px on phones, never fewer
   than two columns (min() against 50% guarantees it). */
.deck-grid {
  display: grid; gap: 1.25rem;
  grid-template-columns: repeat(auto-fill, minmax(min(300px, calc(50% - 0.625rem)), 1fr));
}
.deck-grid > * { max-width: 300px; width: 100%; }

.cat-msg { color: #64748b; padding: 2rem 0; margin: 0; }
.cat-err { color: #b91c1c; }
.linkish { background: none; border: 0; padding: 0; color: #2563eb; font: inherit; text-decoration: underline; cursor: pointer; }

@media (max-width: 991.98px) {
  .feats { grid-template-columns: repeat(2, 1fr); row-gap: 1.4rem; }
  .feat:nth-child(3) { border-left: 0; }
}

@media (max-width: 767.98px) {
  /* On a phone the strip is a stack, and a card overlapping the hero would push
     a tall block of text over the artwork. Sit it below the hero instead. */
  .feats { grid-template-columns: 1fr; row-gap: 1.25rem; margin-top: 1.25rem; }
  .feat + .feat { border-left: 0; }

  /* Narrow viewport + `cover` crops the artwork inward, which walks the
     headphones straight under the headline. Push the focal point right and lay a
     white-to-clear scrim over it, so the copy keeps its contrast and the art
     survives as atmosphere rather than clutter. */
  .hero {
    background-position: 88% center;
    background-image: linear-gradient(100deg, rgba(255,255,255,.94) 0%, rgba(255,255,255,.86) 42%, rgba(255,255,255,.35) 68%, rgba(255,255,255,0) 88%),
                      url("/images/hero-headset.webp");
  }
  /* ONE padding declaration. A `padding-top` earlier in this block was being
     silently overridden by this shorthand, which is why the 64px logo sat on top
     of the headline on every phone. The top value clears the overlaid logo. */
  .hero-wrap { grid-template-columns: 1fr; padding: 6.25rem 1.25rem 2.25rem; }
  .hero-copy { max-width: none; }
  .hero-search { max-width: none; }
  /* The chips were wrapping to SEVEN rows — a 372px-tall control bar before a
     single card was visible. On a phone they become one horizontally-scrollable
     strip (the standard pattern), with difficulty on its own line beneath. */
  .cat-bar { flex-direction: column; align-items: stretch; gap: .75rem; margin-bottom: 1.1rem; }
  .chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* bleed to the screen edges so the strip reads as scrollable */
    margin: 0 -1.25rem; padding: .15rem 1.25rem .5rem;
  }
  .chips::-webkit-scrollbar { display: none; }
  .chip { flex: none; scroll-snap-align: start; }
  .diff { align-self: flex-start; }

  .deck-grid {
    gap: .75rem;
    grid-template-columns: repeat(auto-fill, minmax(min(170px, calc(50% - 0.375rem)), 1fr));
  }
  .deck-grid > * { max-width: 170px; }
}
</style>
